/**
 * Texas TDU delivery charges (DESIGN §3.2). These are pass-through charges that
 * apply regardless of REP. Residential class only.
 *
 * Verification (TODO-002 Step 4, 2026-10-06): checked against the PUCT
 * "Monthly Report, As of October 1, 2026" PDFs (files dated 2026-09-28) at
 * https://ftp.puc.texas.gov/public/puct-info/industry/electric/rates/tdr/tdu/
 * CenterPoint and TNMP match exactly. Oncor fixed charges match; the Oncor
 * volumetric rate in that report is $0.060295/kWh. The 6.8673¢ default is a
 * temporary rate effective 2026-10-04 (PUCT Docket No. 59249, TCRF/DCRF update,
 * subject to refund) that only appears in secondary sources. The PUCT value is
 * kept as an alternate.
 */
import type { RateEntry, RegionId, TduId } from "./types";

const PUCT_DIR = "https://ftp.puc.texas.gov/public/puct-info/industry/electric/rates/tdr/tdu/";
const puct = (file: string): string =>
  `PUCT Monthly Report as of 10/1/2026 (${PUCT_DIR}${file}), verified 2026-10-06`;

export interface TduConfig {
  readonly id: TduId;
  readonly name: string;
  /** Monthly customer charge, $/month. */
  readonly customerCharge: RateEntry<number>;
  /** Monthly metering charge, $/month. */
  readonly meteringCharge: RateEntry<number>;
  /** Volumetric delivery charge on every imported kWh, $/kWh. */
  readonly volumetricRate: RateEntry<number>;
  /** Region preselected when the rep picks this TDU. */
  readonly defaultRegion: RegionId;
}

export const TDUS: Readonly<Record<TduId, TduConfig>> = {
  oncor: {
    id: "oncor",
    name: "Oncor",
    customerCharge: { value: 1.48, unit: "$/month", effectiveDate: "2026-10-01", source: puct("Oncor_Rate_Report.pdf") },
    meteringCharge: { value: 2.58, unit: "$/month", effectiveDate: "2026-10-01", source: puct("Oncor_Rate_Report.pdf") },
    volumetricRate: {
      value: 0.068673,
      unit: "$/kWh",
      effectiveDate: "2026-10-04",
      source:
        "Secondary-source only (theday.company, energychoicematters.com citing PUCT): temporary rate under PUCT Docket No. 59249 (TCRF/DCRF update), effective 2026-10-04",
      note: "Temporary rate, subject to refund. The PUCT 10/1/2026 report shows 6.0295¢ (pre-Docket 59249); the rep can switch to it.",
      alternates: [
        {
          value: 0.060295,
          effectiveDate: "2026-10-01",
          source: puct("Oncor_Rate_Report.pdf"),
          label: "Pre-Docket 59249",
        },
      ],
    },
    defaultRegion: "dfw",
  },
  centerpoint: {
    id: "centerpoint",
    name: "CenterPoint",
    customerCharge: { value: 2.11, unit: "$/month", effectiveDate: "2026-10-01", source: puct("CenterPoint_Rate_Report.pdf") },
    meteringCharge: { value: 2.79, unit: "$/month", effectiveDate: "2026-10-01", source: puct("CenterPoint_Rate_Report.pdf") },
    volumetricRate: { value: 0.065333, unit: "$/kWh", effectiveDate: "2026-10-01", source: puct("CenterPoint_Rate_Report.pdf") },
    defaultRegion: "gulf",
  },
  tnmp: {
    id: "tnmp",
    name: "TNMP",
    customerCharge: { value: 1.7, unit: "$/month", effectiveDate: "2026-10-01", source: puct("TNMP_Rate_Report.pdf") },
    meteringCharge: { value: 5.86, unit: "$/month", effectiveDate: "2026-10-01", source: puct("TNMP_Rate_Report.pdf") },
    volumetricRate: { value: 0.07771, unit: "$/kWh", effectiveDate: "2026-10-01", source: puct("TNMP_Rate_Report.pdf") },
    defaultRegion: "gulf",
  },
};

export const TDU_IDS: readonly TduId[] = ["oncor", "centerpoint", "tnmp"] as const;

/** Total fixed TDU charge, $/month (customer + metering). */
export function tduFixedMonthly(tdu: TduConfig): number {
  return tdu.customerCharge.value + tdu.meteringCharge.value;
}
